<script setup>
import { computed, ref, watch } from 'vue'
import { useActionCenterStore } from '../../stores/ActionCenterStore'
import { storeToRefs } from 'pinia'
import BookRequirementDeadlines from './BookRequirementDeadlines.vue'
import BookAllEmployersView from './BookAllEmployersView.vue'
import BcsSearchDropdown from '../BcsSearchDropdown.vue'
import { aggregateRequirementsFromFlatEvents, extractActiveEventsFromPlanYears } from './helpers'
import { BOOK_VIEWS, DEADLINE_VIEWS, EMPLOYER_TABLE_VIEWS } from './constants'

const actionCenterStore = useActionCenterStore()
const { filteredPlanYears, bookDetailsView } = storeToRefs(actionCenterStore)

const activeRequirementEvents = computed(() => extractActiveEventsFromPlanYears(filteredPlanYears.value))

const requirementsByTypeCount = computed(() => {
  return aggregateRequirementsFromFlatEvents(activeRequirementEvents.value).length
})

const requirementsByEmployerCount = computed(() => {
  return new Set(activeRequirementEvents.value.map(event => event.employerId)).size
})

const upcomingRequirementsCount = computed(() => {
  const start = new Date()
  const end = new Date()
  end.setDate(end.getDate() + 30)

  const events = extractActiveEventsFromPlanYears(filteredPlanYears.value, { dateStart: start, dateEnd: end })
  return new Set(events.map(event => event.employerId)).size
})

const isDeadlineView = computed(() => DEADLINE_VIEWS.includes(bookDetailsView.value))
const isEmployerTableView = computed(() => EMPLOYER_TABLE_VIEWS.includes(bookDetailsView.value))

const highRiskCount = computed(() => {
  return filteredPlanYears.value.filter(py =>
    py.assessment_status === 4 && Number(py.percentage_compliance_risk || 0) < 50
  ).length
})

const incompleteAssessmentsCount = computed(() => {
  return filteredPlanYears.value.filter(py => py.assessment_status !== 4).length
})

const incompleteActionsCount = computed(() => {
  return filteredPlanYears.value.filter(py =>
    py.assessment_status === 4 && Array.isArray(py.action_items) && py.action_items.length > 0
  ).length
})

const viewOptions = computed(() => {
  const allCount = filteredPlanYears.value.length

  return [
    { key: BOOK_VIEWS.ALL, label: 'All', count: allCount },
    { key: BOOK_VIEWS.HIGH_RISK, label: 'High Risk Scores', count: highRiskCount.value },
    { key: BOOK_VIEWS.INCOMPLETE_ACTIONS, label: 'Incomplete Action Items', count: incompleteActionsCount.value },
    { key: BOOK_VIEWS.INCOMPLETE_ASSESSMENTS, label: 'Incomplete Assessments', count: incompleteAssessmentsCount.value },
    { key: BOOK_VIEWS.UPCOMING_REQUIREMENTS, label: 'Upcoming Calendar Requirements', count: upcomingRequirementsCount.value },
    { key: BOOK_VIEWS.REQUIREMENTS_BY_EMPLOYER, label: 'Calendar Requirements by Employer', count: requirementsByEmployerCount.value },
    { key: BOOK_VIEWS.REQUIREMENTS_BY_TYPE, label: 'Calendar Requirements by Type', count: requirementsByTypeCount.value }
  ]
})

const selectView = (key) => {
  actionCenterStore.setBookDetailsView(key)
}

const viewDescriptionText = computed(() => {
  const descriptions = {
    [BOOK_VIEWS.ALL]: `This view will show all employer rows for ${filteredPlanYears.value.length} employers.`,
    [BOOK_VIEWS.HIGH_RISK]: 'This view will show employers ordered by risk score with the tasks that correspond to the high-risk workflow.',
    [BOOK_VIEWS.INCOMPLETE_ACTIONS]: 'This view will show employers with incomplete action items and the tasks available for resolving them.',
    [BOOK_VIEWS.INCOMPLETE_ASSESSMENTS]: 'This view will show employers with incomplete questionnaires or assessments and the tasks needed to complete them.',
    [BOOK_VIEWS.UPCOMING_REQUIREMENTS]: `This view is showing ${upcomingRequirementsCount.value} upcoming requirement records due within the next 30 days.`,
    [BOOK_VIEWS.REQUIREMENTS_BY_TYPE]: `This view is showing ${requirementsByTypeCount.value} requirement groups.`,
    [BOOK_VIEWS.REQUIREMENTS_BY_EMPLOYER]: `This view is showing ${requirementsByEmployerCount.value} employers with active calendar requirements.`
  }
  return descriptions[bookDetailsView.value] || ''
})

const resetTableView = () => {
  actionCenterStore.resetBookDetailsView()
}

// ===== SHARED SEARCH =====
const searchTerm = ref('')
const searchDropdownRef = ref(null)
const bookAllEmployersRef = ref(null)
const bookRequirementDeadlinesRef = ref(null)

const searchItems = computed(() => {
  if (isEmployerTableView.value && bookAllEmployersRef.value) {
    return bookAllEmployersRef.value.allRows || []
  }
  if (isDeadlineView.value && bookRequirementDeadlinesRef.value) {
    const child = bookRequirementDeadlinesRef.value
    return child.groupBy === 'employer'
      ? (child.employersWithEvents || [])
      : (child.requirementsGroups || [])
  }
  return []
})

const searchFields = computed(() => {
  if (isDeadlineView.value && bookRequirementDeadlinesRef.value) {
    return bookRequirementDeadlinesRef.value.groupBy === 'requirement'
      ? ['requirementName']
      : ['employerName']
  }
  return ['employerName']
})

const searchArrayTerm = computed(() => {
  if (isDeadlineView.value && bookRequirementDeadlinesRef.value) {
    return bookRequirementDeadlinesRef.value.groupBy === 'requirement'
      ? 'requirementName'
      : 'employerName'
  }
  return 'employerName'
})

watch(bookDetailsView, () => {
  searchTerm.value = ''
  if (searchDropdownRef.value) {
    searchDropdownRef.value.clearSearch()
  }
})

function handleSearch(payload) {
  searchTerm.value = payload.term
}

function handleClearSearch() {
  searchTerm.value = ''
}
</script>

<template>
  <div id="bookTableManagerSection" class="book-details-container bcs-page-padding">
    <!-- Section Header -->
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h2 class="header-20 mb-0"><strong>Book Details</strong></h2>
      <div class="search-book-overview">
        <BcsSearchDropdown
          id="bookOverviewSearch"
          ref="searchDropdownRef"
          :search-fields="searchFields"
          :array-term="searchArrayTerm"
          :showResults="false"
          placeholder="Search Book Overview Table"
          :items="searchItems"
          :search-button-text="''"
          @search="handleSearch"
          @clear="handleClearSearch"
        />
      </div>
    </div>

    <!-- View Selector Bar -->
    <div class="view-selector-bar">
      <div class="d-flex align-items-center flex-wrap gap-2">
        <span class="view-label text-16"><med>Viewing:</med></span>
        <button
          v-for="option in viewOptions"
          :key="option.key"
          class="btn btn-outline-primary-toggle view-pill"
          :class="{ active: bookDetailsView === option.key }"
          @click="selectView(option.key)"
        >
          <template v-if="option.count !== null">({{ option.count }}) </template>{{ option.label }}
        </button>
      </div>
    </div>

    <!-- View Description -->
    <div class="view-description mt-3">
      <span class="text-14 text-gray-600">
        {{ viewDescriptionText }}
        <a
          v-if="bookDetailsView !== BOOK_VIEWS.ALL"
          href="#"
          class="text-primary text-decoration-none ms-2"
          @click.prevent="resetTableView"
        >
          <med>Reset Table View</med>
        </a>
      </span>
    </div>

    <div class="table-placeholder mt-3">
      <BookRequirementDeadlines
        v-if="isDeadlineView"
        ref="bookRequirementDeadlinesRef"
        :view-mode="bookDetailsView"
        :search-term="searchTerm"
      />

      <BookAllEmployersView
        v-else-if="isEmployerTableView"
        ref="bookAllEmployersRef"
        :view-mode="bookDetailsView"
        :search-term="searchTerm"
      />

      <div v-else class="book-details-placeholder">
        <p class="header-18 mb-2"><strong>Coming Soon</strong></p>
        <p class="text-16 text-gray-600 mb-0">
          This view will be available soon.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.book-details-container {
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.view-selector-bar {
  background-color: #F8F9FA;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.view-label {
  color: #495057;
  margin-right: 0.25rem;
}

.view-pill {
  padding: 0.3rem 0.75rem;
  border-radius: 3.2px;
  font-size: 0.875rem;
  white-space: nowrap;
}

.view-description {
  color: #6c757d;
}

.book-details-placeholder {
  background-color: #fff;
  border: 1px solid #C5CDD4;
  border-radius: 8px;
  padding: 24px;
}

.search-book-overview {
  width: 320px;
}
</style>
